import { SttFragment } from '@ftr/contracts/regional-api/stt'
import { assert } from '@ftr/contracts/shared/Assertions'
import { generateUuid, LocalDateTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { DefaultTimeStringParseMethod, isNotNullOrUndefined, splitPascalCase, TimeStringParser } from '@ftr/foundation'
import { SttEdit } from '@ftr/regional-api-contracts'
import { LocalDateTime, LocalTime } from '@js-joda/core'
import { RealTimeSttRemark, RealTimeSttSegmentMap } from '../../../types'
import { STT_FRAGMENT_SEPARATOR } from '../edit-operations'
import { OperationField, OperationResult } from './operation-result'
import { bumpStartTimeEditsToProduceUniqueTime } from './unique-start-time-utils'
import { boundaryErrorToValidationResult, checkEditForBoundaryErrors } from './within-boundaries-utils'

/**
 * Requires at least one of startTimeInput or endTimeInput to be provided
 */
export interface EditRemarkBoundingTimesOperation {
  type: 'editRemarkBoundingTimes'
  remarkId: Uuid
  // Undefined if it has not been edited and should instead be looked up in the segment map (preserves precision)
  startTime: string | undefined
  // Undefined if it has not been edited and should instead be looked up in the segment map (preserves precision)
  endTime: string | undefined
}

export function editRemarkBoundingTimesTransaction(
  operation: EditRemarkBoundingTimesOperation,
  segmentMap: RealTimeSttSegmentMap,
): OperationResult {
  // Find the remark in the segment map
  const remark = segmentMap.findRemark(operation.remarkId)
  if (!remark) {
    throw new Error(`Remark with ID ${operation.remarkId} not found in segment map`)
  }

  assert(
    operation.startTime !== undefined || operation.endTime !== undefined,
    'Start time or end time must be defined for bounding times operation',
  )
  const newStartTime =
    operation.startTime !== undefined ? safeParseTime(operation.startTime) : remark.startTime.toLocalTime()
  const newEndTime = operation.endTime !== undefined ? safeParseTime(operation.endTime) : remark.endTime.toLocalTime()
  if (!newStartTime || !newEndTime) {
    const invalidFields: OperationField[] = [
      ...(newStartTime ? [] : (['startTime'] as const)),
      ...(newEndTime ? [] : (['endTime'] as const)),
    ]
    const invalidFieldsMessagePart = invalidFields.map(field => splitPascalCase(field).toLowerCase()).join(' and ')
    return {
      type: 'validationError',
      message: `Please enter a valid ${invalidFieldsMessagePart}.`,
      invalidFields,
    }
  }

  const operationStartTime = remark.startTime.toLocalDate().atTime(newStartTime)
  const operationEndTime = remark.endTime.toLocalDate().atTime(newEndTime)

  if (operationStartTime.isAfter(operationEndTime)) {
    return {
      type: 'validationError',
      message: 'Start time must be before end time.',
      invalidFields: ['startTime', 'endTime'],
    }
  }

  const segmentForNewTime = segmentMap.getSegmentAt(operationStartTime)
  assert(segmentForNewTime)

  const editsToEnsureUniqueStartTime = bumpStartTimeEditsToProduceUniqueTime(
    operationStartTime,
    segmentMap.withDeletedRemark(remark.id),
    'forward',
  )
  const editsToBoundRemark = computeEditsToBoundRemark(
    new LocalDateTimeRange(operationStartTime, operationEndTime),
    remark,
  )
  const boundaryErrors = editsToBoundRemark
    .map(edit => checkEditForBoundaryErrors(edit, segmentMap))
    .filter(isNotNullOrUndefined)
  if (boundaryErrors.length) {
    return boundaryErrorToValidationResult(boundaryErrors[0])
  }

  return {
    type: 'transaction',
    transaction: {
      id: generateUuid(),
      edits: [...editsToEnsureUniqueStartTime, ...editsToBoundRemark],
    },
    sideEffects: [
      {
        type: 'scrollToRemark',
        remarkId: remark.id,
        segmentId: segmentForNewTime.id,
      },
    ],
  }
}

function computeEditsToBoundRemark(boundingTimeRange: LocalDateTimeRange, remark: RealTimeSttRemark): SttEdit[] {
  assert(remark.fragments, 'Remarks require fragments for editing.')
  // For an empty utterance we need to delete and soft create it again
  if (remark.fragments.length === 0) {
    return [
      {
        type: 'deleteUtterance',
        utteranceId: remark.id,
      },
      {
        type: 'softCreateUtterance',
        startTime: boundingTimeRange.start,
        endTime: boundingTimeRange.end,
        utteranceId: remark.id,
        dialect: remark.dialect ?? null,
        speakerId: remark.speakerId,
      },
    ]
  }

  const boundedFragments = remark.fragments.map((fragment, index, fragments) => ({
    ...fragment,
    // Always update first fragment start and last fragments end times
    startTime: index === 0 ? boundingTimeRange.start : boundByTimeRange(fragment.startTime, boundingTimeRange),
    endTime:
      index === fragments.length - 1 ? boundingTimeRange.end : boundByTimeRange(fragment.endTime, boundingTimeRange),
  }))
  // Fragments should not share the same start time otherwise the content will not be display in the correct order
  const updatedFragments = mergeFragmentsToEnsureUniqueStartTimes(boundedFragments)

  return [
    {
      type: 'upsertUtterance',
      utteranceId: remark.id,
      fragments: updatedFragments,
      dialect: remark.dialect ?? null,
      speakerId: remark.speakerId,
    },
  ]
}

function mergeFragmentsToEnsureUniqueStartTimes(fragments: SttFragment[]): SttFragment[] {
  const mergedFragments: SttFragment[] = []
  for (const fragment of fragments) {
    const lastFragment = mergedFragments.at(-1)
    const matchingStartTimes = lastFragment && lastFragment.startTime.equals(fragment.startTime)
    const consecutive = lastFragment && lastFragment.endTime.equals(fragment.startTime)
    const fragmentZeroDuration = fragment.startTime.equals(fragment.endTime)
    const mergeWithLast = matchingStartTimes || (consecutive && fragmentZeroDuration)
    if (!mergeWithLast) {
      mergedFragments.push(fragment)
      continue
    }

    mergedFragments[mergedFragments.length - 1] = {
      startTime: lastFragment.startTime,
      endTime: fragment.endTime,
      content: lastFragment.content + STT_FRAGMENT_SEPARATOR + fragment.content,
    }
  }
  return mergedFragments
}

function boundByTimeRange(original: LocalDateTime, boundingTimeRange: LocalDateTimeRange): LocalDateTime {
  if (original.isBefore(boundingTimeRange.start)) {
    return boundingTimeRange.start
  }
  if (original.isAfter(boundingTimeRange.end)) {
    return boundingTimeRange.end
  }
  return original
}

function safeParseTime(value: string): LocalTime | undefined {
  try {
    return TimeStringParser.parse(value, [DefaultTimeStringParseMethod])
  } catch {
    return undefined
  }
}
